import { useState } from 'react';
import TeamBackButton from '@/components/team-assignments/TeamBackButton';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { HeaderSkeleton, ProfileGuideButton } from '@/components/profile';
import { ROLES_PERMISSIONS, ROUTES } from '@/constants';
import { useAuthStore } from '@/store/useAuthStore';
import {
  hasRequiredRoutesPermissions,
  hasRequiredRoutesRoles,
} from '@/utils/authUtils';
import { DATE_FORMATS } from '@/utils/formUtils';
import CompanyOzProfileModal from './CompanyOzProfileModal';

const initials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

/**
 * Teal header of the company profile: identity on the left, links to the
 * company's employees, timesheet, HR requests, sectors, invoices and (for the roles
 * that run payroll) its OZ profile on the right.
 */
export default function CompanyProfileHeader({
  company,
  companyId,
  isLoading,
  hasError,
  onOpenGuide,
}) {
  const { t } = useTranslation(['menus', 'forms', 'translations']);
  const tc = (key, opts) =>
    t(`companyProfile.${key}`, { ns: 'translations', ...opts });

  const [ozOpen, setOzOpen] = useState(false);
  const user = useAuthStore((state) => state.user);
  const roles = user?.roles || [];
  // The same check the target page runs, so a link never ends on "access denied".
  const canOpen = ({ requiredRoles, requiredPermissions }) =>
    hasRequiredRoutesRoles(roles, requiredRoles) &&
    hasRequiredRoutesPermissions(user?.permissions || [], requiredPermissions);
  // Role-only gate, like the /oz route itself (OZ.VIEW lists no permissions).
  const canSeeOz = roles.some((r) =>
    ROLES_PERMISSIONS.OZ.VIEW.requiredRoles.includes(r)
  );

  const gd = company?.generalData || {};
  const status = company?.status;
  const now = dayjs();
  const links = [
    {
      key: 'employees',
      label: tc('linkEmployees'),
      to: `${ROUTES.admin}/${ROUTES.employees}?company=${companyId}&offset=1&limit=10`,
    },
    {
      key: 'timesheet',
      label: tc('linkTimesheet'),
      // Always with the company: without it the page picks the first one.
      to: `${ROUTES.admin}/${ROUTES.time_sheet}?company=${companyId}&offset=1&limit=10&month=${now.month() + 1}&year=${now.year()}`,
    },
    company?.applicationSettings?.isHr && {
      key: 'hr',
      label: tc('linkHrRequests'),
      to: `${ROUTES.admin}/${ROUTES.hrRequest}?company=${companyId}`,
    },
    canOpen(ROLES_PERMISSIONS.SECTORS.VIEW) &&
      company?.applicationSettings?.isSector && {
        key: 'sectors',
        label: tc('linkSectors'),
        to: `${ROUTES.admin}/${ROUTES.sectors}?company=${companyId}&offset=1&limit=10`,
      },
    canOpen(ROLES_PERMISSIONS.E_INVOICES.VIEW) && {
      key: 'invoices',
      label: tc('linkInvoices'),
      to: `${ROUTES.admin}/${ROUTES.outgoingInvoices}?company=${companyId}`,
    },
  ].filter(Boolean);

  return (
    <header className="flex flex-wrap items-center justify-between gap-3 bg-accent px-4 pb-1 pt-2.5 text-white xl:flex-nowrap">
      {/* On one row with the actions from xl up: the name and the line under
          it give way (cut short) so the buttons never drop below. */}
      <div
        data-tour="profile-header"
        className="flex min-w-0 items-center gap-3 xl:flex-1"
      >
        <Link
          to={`${ROUTES.admin}/${ROUTES.companies}`}
          aria-label={t('back', { ns: 'menus' })}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 hover:text-white"
        >
          <ArrowLeftOutlined />
        </Link>
        {hasError ? null : isLoading ? (
          <HeaderSkeleton />
        ) : (
          <>
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white font-extrabold text-accent">
              {initials(company?.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className="truncate text-xl font-extrabold"
                  title={company?.name}
                >
                  {company?.name}
                </span>
                {status && (
                  <span className="shrink-0 whitespace-nowrap rounded-full bg-white px-2.5 py-0.5 text-[11px] font-semibold text-accent">
                    {typeof status === 'object' ? status.label : status}
                  </span>
                )}
              </div>
              <div className="truncate text-[13px] opacity-95">
                {[
                  gd.code && `MB ${gd.code}`,
                  gd.pib && `PIB ${gd.pib}`,
                  gd.city,
                  company?.createdAt &&
                    tc('onPlatformSince', {
                      date: dayjs(company.createdAt).format(
                        DATE_FORMATS.DISPLAY
                      ),
                    }),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
          </>
        )}
      </div>

      {company && (
        <div
          data-tour="profile-actions"
          className="employee-profile__actions ml-auto flex flex-wrap items-center justify-end gap-2 xl:shrink-0 xl:flex-nowrap">
          {/* HLB team board (team assignments), for HLB staff only. */}
          <TeamBackButton companyId={companyId} variant="profile" />
          {links.map((link) => (
            <Link
              key={link.key}
              to={link.to}
              className={`employee-profile__hb ${link.className || ''}`}
            >
              {link.label}
            </Link>
          ))}
          {canSeeOz && (
            <button
              type="button"
              className="employee-profile__hb !border-[#FFCC00]"
              onClick={() => setOzOpen(true)}
            >
              {tc('linkOzProfile')}
            </button>
          )}
          <ProfileGuideButton onClick={onOpenGuide} />
        </div>
      )}
      {canSeeOz && companyId && ozOpen && (
        <CompanyOzProfileModal
          open={ozOpen}
          onClose={() => setOzOpen(false)}
          companyId={companyId}
          companyName={company?.name}
        />
      )}
    </header>
  );
}
